import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { Roles } from '~/data/user';
import { fetchFromOldApi } from '~/services/Http';
import { hasOneOfRoles } from '~/services/Permissions';

import {
  BUUnitData,
  BusinessUnit,
  OrgTreeData,
  OrganizationUnit,
} from './types';

export type StoreState = {
  orgUnits: OrganizationUnit[];
  orgUnitsIncludeDeleted: OrganizationUnit[];
  orgTree: OrgTreeData[];
  aMyDeputyUnits: number[];
  aMyAltUnits: number[];
  aMyManUnits: number[];
  aMyHRUnits: number[];
  isLoading: boolean;
  failedToLoad: boolean;
  hasData: boolean;
};

const initialState: StoreState = {
  orgUnits: [],
  orgUnitsIncludeDeleted: [],
  orgTree: [],
  aMyDeputyUnits: [],
  aMyAltUnits: [],
  aMyManUnits: [],
  aMyHRUnits: [],
  isLoading: false,
  failedToLoad: false,
  hasData: false,
};

export enum OrgUnitsActionKeyPrefixes {
  FETCH_ORGANIZATION_UNITS = 'orgUnits/FETCH_ORGANIZATION_UNITS',
}

export type OrganizationUnitsPayload = {
  orgUnits: OrganizationUnit[];
  orgUnitsIncludeDeleted: OrganizationUnit[];
  orgTree: OrgTreeData[];
  aMyDeputyUnits: number[];
  aMyAltUnits: number[];
  aMyManUnits: number[];
  aMyHRUnits: number[];
};

export const getOrgTree = (): Promise<BusinessUnit[]> => {
  return fetchFromOldApi(`/json/org/tree`).then((r) => r.json());
};

export const getOrgTreeUnits = (unitNumber: number): Promise<BUUnitData[]> =>
  fetchFromOldApi(`/json/org/tree/${unitNumber}/unit`).then((r) => r.json());

export const getOrganizationUnits = (): Promise<OrganizationUnit[]> =>
  fetchFromOldApi(`/json/org/unit?includeInactive=true`).then((r) => r.json());

export const getSubUnits = (id: number, role?: string): Promise<number[]> =>
  role && role !== Roles.MANAGER
    ? fetchFromOldApi(`/json/org/tree/${role}/${id}/unitsub`).then((r) =>
        r.json(),
      )
    : fetchFromOldApi(`/json/org/tree/1/manager/${id}/unitsub`).then((r) =>
        r.json(),
      );

type FetchOrgUnitsProps = {
  myRoles: string[];
  myId: number;
};

export const fetchOrganizationUnits = createAsyncThunk<
  OrganizationUnitsPayload,
  FetchOrgUnitsProps
>(
  OrgUnitsActionKeyPrefixes.FETCH_ORGANIZATION_UNITS,
  async ({ myRoles, myId }): Promise<OrganizationUnitsPayload> => {
    const shouldLoad = {
      aMyDeputyUnits: myRoles.includes(Roles.DEPUTY_MANAGER),
      aMyAltUnits: myRoles.includes(Roles.ALT_MANAGER),
      aMyManUnits: myRoles.includes(Roles.MANAGER),
      aMyHRUnits: hasOneOfRoles(myRoles, [
        Roles.HR_STAFF,
        Roles.HR_ADMIN,
        Roles.HR_ADMIN_LIMITED,
        Roles.HR_SALARY,
        Roles.HR_DEV,
      ]),
    };

    const promiseMyUnitsData = Promise.all([
      shouldLoad.aMyDeputyUnits
        ? getSubUnits(myId, Roles.DEPUTY_MANAGER)
        : Promise.resolve([]),
      shouldLoad.aMyAltUnits
        ? getSubUnits(myId, Roles.ALT_MANAGER)
        : Promise.resolve([]),
      shouldLoad.aMyManUnits
        ? getSubUnits(myId, Roles.MANAGER)
        : Promise.resolve([]),
      shouldLoad.aMyHRUnits ? getSubUnits(myId, 'HR') : Promise.resolve([]),
    ]).then(
      ([aMyDeputyUnits, aMyAltUnits, aMyManUnits, aMyHRUnits]): Pick<
        OrganizationUnitsPayload,
        'aMyDeputyUnits' | 'aMyAltUnits' | 'aMyManUnits' | 'aMyHRUnits'
      > => {
        return {
          aMyDeputyUnits,
          aMyAltUnits,
          aMyManUnits,
          aMyHRUnits,
        };
      },
    );

    return Promise.all([
      getOrganizationUnits().then(
        (
          allOrgUnits,
        ): Pick<
          OrganizationUnitsPayload,
          'orgUnitsIncludeDeleted' | 'orgUnits'
        > => {
          return {
            orgUnitsIncludeDeleted: allOrgUnits,
            orgUnits: allOrgUnits.filter(
              (unit) => unit.fOrgUnitIsActive !== false,
            ),
          };
        },
      ),
      getOrgTree().then(
        (orgTree): Promise<OrgTreeData[]> =>
          Promise.all(
            orgTree.map(
              (unit): Promise<OrgTreeData> =>
                getOrgTreeUnits(unit.fTreeId).then((units): OrgTreeData => {
                  return {
                    ...unit,
                    units,
                  };
                }),
            ),
          ),
      ),
      promiseMyUnitsData,
    ]).then(([orgUnitData, orgTree, myUnitsData]): OrganizationUnitsPayload => {
      return {
        ...orgUnitData,
        orgTree,
        ...myUnitsData,
      };
    });
  },
);

export const orgUnitsSlice = createSlice({
  name: 'orgUnits',
  initialState,
  reducers: {
    updateOrganizationUnit: (
      state,
      action: PayloadAction<OrganizationUnit>,
    ) => {
      const index = state.orgUnits.findIndex(
        (unit) => unit.fOrgUnitId === action.payload.fOrgUnitId,
      );
      if (index !== -1) {
        state.orgUnits[index] = action.payload;
      }
    },
    createOrganizationUnit: (
      state,
      action: PayloadAction<OrganizationUnit>,
    ) => {
      state.orgUnits.push(action.payload);
    },
    deleteOrganizationUnit: (
      state,
      action: PayloadAction<OrganizationUnit>,
    ) => {
      const index = state.orgUnits.findIndex(
        (unit) => unit.fOrgUnitId === action.payload.fOrgUnitId,
      );
      if (index !== -1) {
        state.orgUnits.splice(index, 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchOrganizationUnits.fulfilled,
        (state, action: PayloadAction<OrganizationUnitsPayload>) => {
          state.failedToLoad = false;
          state.isLoading = false;
          state.hasData = true;
          state.orgTree = action.payload.orgTree;
          state.orgUnits = action.payload.orgUnits;
          state.aMyAltUnits = action.payload.aMyAltUnits;
          state.aMyDeputyUnits = action.payload.aMyDeputyUnits;
          state.aMyHRUnits = action.payload.aMyHRUnits;
          state.aMyManUnits = action.payload.aMyManUnits;
          state.orgUnitsIncludeDeleted = action.payload.orgUnitsIncludeDeleted;
        },
      )
      .addCase(fetchOrganizationUnits.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchOrganizationUnits.rejected, (state) => {
        state.failedToLoad = true;
      });
  },
});

export const {
  createOrganizationUnit,
  updateOrganizationUnit,
  deleteOrganizationUnit,
} = orgUnitsSlice.actions;
export const orgUnitReducer = orgUnitsSlice.reducer;
